export type AudioBufferPlayerOptions = {
  loop?: boolean;
  volume?: number; // 0..1
  onEnded?: () => void;
};

export class AudioBufferPlayer {
  private ctx: AudioContext;
  private gain: GainNode;

  private buffer: AudioBuffer | null = null;
  private source: AudioBufferSourceNode | null = null;

  private startTime = 0;
  private offset = 0;
  private playing = false;

  constructor(ctx?: AudioContext, initialVolume = 1) {
    this.ctx = ctx ?? new AudioContext();
    this.gain = this.ctx.createGain();
    this.gain.gain.value = clamp01(initialVolume);
    this.gain.connect(this.ctx.destination);
  }

  load(buffer: AudioBuffer) {
    this.buffer = buffer;
    this.stop();
  }

  async resumeAudioContext() {
    if (this.ctx.state !== 'running') {
      await this.ctx.resume();
    }
  }

  setVolume(v: number) {
    this.gain.gain.value = clamp01(v);
  }

  isPlaying() {
    return this.playing;
  }

  play(opts: AudioBufferPlayerOptions = {}, startAtSeconds?: number) {
    if (!this.buffer) {
      throw new Error('No AudioBuffer loaded. Call load(buffer) first.');
    }

    if (this.playing) {
      this.stop();
    }

    const { loop = false, volume, onEnded } = opts;

    if (typeof volume === 'number') {
      this.setVolume(volume);
    }

    const offset =
      typeof startAtSeconds === 'number' ? startAtSeconds : this.offset;
    this.offset = clampRange(offset, 0, this.buffer.duration);

    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.loop = loop;
    src.connect(this.gain);

    src.onended = () => {
      if (!this.playing) {
        return;
      }

      this.playing = false;
      this.source = null;

      if (!loop) {
        this.offset = this.buffer ? this.buffer.duration : 0;
      }

      onEnded?.();
    };

    this.source = src;
    this.startTime = this.ctx.currentTime;
    this.playing = true;

    src.start(0, this.offset);
  }

  pause() {
    if (!this.playing || !this.source) {
      return;
    }

    const elapsed = this.ctx.currentTime - this.startTime;
    this.offset += elapsed;

    this.playing = false;
    this.source.onended = null;
    this.source.stop();
    this.source.disconnect();
    this.source = null;
  }

  resume(opts: AudioBufferPlayerOptions = {}) {
    if (this.playing) {
      return;
    }

    this.play(opts);
  }

  stop(keepOffset = false) {
    if (this.source) {
      this.playing = false;
      this.source.onended = null;

      try {
        this.source.stop();
      } catch {
        // ignore if already stopped
      }

      this.source.disconnect();
      this.source = null;
    }

    if (!keepOffset) {
      this.offset = 0;
    }
  }

  seek(seconds: number, opts: AudioBufferPlayerOptions = {}) {
    if (!this.buffer) {
      throw new Error('No AudioBuffer loaded. Call load(buffer) first.');
    }

    this.offset = clampRange(seconds, 0, this.buffer.duration);

    if (this.playing) {
      this.play(opts, this.offset);
    }
  }

  getCurrentTimeSeconds() {
    if (!this.buffer) {
      return 0;
    }

    if (!this.playing) {
      return clampRange(this.offset, 0, this.buffer.duration);
    }

    const elapsed = this.ctx.currentTime - this.startTime;
    return clampRange(this.offset + elapsed, 0, this.buffer.duration);
  }

  getAudioContext() {
    return this.ctx;
  }
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function clampRange(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}
